import React from 'react';
import { API_URL } from '../../utils/api';

/**
 * Formulär för en mail-only projektörskontakt (ProjektorContact).
 *
 * Delas av Dashboard → Projektörer (ProjektorContactAdmin) och av
 * Projektörer-modalen, så att de två vyerna inte kan glida isär. Låg
 * ambitionsnivå med flit: samma fält och samma endpoints som förut.
 */

// Motsvarar PROJEKTOR_TYPES i backend/src/models/ProjektorContact.js.
// Fältet har ingen enum-validering i schemat, så listorna kan drifta utan att
// något klagar — ändras den ena bör den andra ändras samtidigt.
export const PROJEKTOR_TYPES = ['Uppmätning bostad', 'Konstruktör', 'Fönster', 'Projektör'];

export const tomtProjektorFormular = {
  companyName: '',
  type: 'Projektör',
  contactPerson: '',
  email: '',
  additionalEmails: '',
  phone: '',
  notes: '',
  isActive: true
};

/**
 * Bygg formulärvärden från en kontakt. Tar både ett rått ProjektorContact-
 * dokument (Dashboard) och en post ur /api/projektorer-listan (modalen) —
 * fälten heter likadant i båda.
 */
export const formularFranKontakt = (c) => ({
  companyName: c?.companyName || '',
  type: c?.type || 'Projektör',
  contactPerson: c?.contactPerson || '',
  email: c?.email || '',
  additionalEmails: Array.isArray(c?.additionalEmails)
    ? c.additionalEmails.join(', ')
    : (c?.additionalEmails || ''),
  phone: c?.phone || '',
  notes: c?.notes || '',
  isActive: c?.isActive !== false
});

/**
 * Spara en kontakt. Skapar om `id` saknas, uppdaterar annars.
 * Kastar vid fel så anroparen kan visa meddelandet.
 */
export const sparaProjektorKontakt = async (id, form) => {
  const token = localStorage.getItem('token');
  const url = id
    ? `${API_URL}/api/projektorer/contacts/${id}`
    : `${API_URL}/api/projektorer/contacts`;

  const res = await fetch(url, {
    method: id ? 'PATCH' : 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(form)
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Kunde inte spara');
  }
  return data.data;
};

/**
 * Ta bort ur listan utan att radera. Posten finns kvar i databasen, så
 * tidigare utskick i contactedProjektorer behåller sin referens och en
 * felklickning går att ångra.
 */
export const inaktiveraProjektorKontakt = async (id) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_URL}/api/projektorer/contacts/${id}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ isActive: false })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Kunde inte ta bort kontakten');
  }
};

const ProjektorContactForm = ({ form, setForm, onSubmit, onCancel, saving, error, isEdit }) => (
  <form onSubmit={onSubmit} className="p-5 space-y-3">
    <h3 className="text-lg font-semibold text-gray-900">
      {isEdit ? 'Redigera kontakt' : 'Ny projektör-kontakt'}
    </h3>
    {error && <div className="bg-red-50 text-red-700 p-2 rounded text-sm">{error}</div>}

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Typ</label>
      <select
        value={form.type}
        onChange={(e) => setForm(f => ({ ...f, type: e.target.value }))}
        className="w-full px-3 py-2 border border-gray-300 rounded-md"
      >
        {PROJEKTOR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
      </select>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Företag</label>
      <input
        type="text"
        value={form.companyName}
        onChange={(e) => setForm(f => ({ ...f, companyName: e.target.value }))}
        className="w-full px-3 py-2 border border-gray-300 rounded-md"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Kontaktperson</label>
      <input
        type="text"
        value={form.contactPerson}
        onChange={(e) => setForm(f => ({ ...f, contactPerson: e.target.value }))}
        className="w-full px-3 py-2 border border-gray-300 rounded-md"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">E-post *</label>
      <input
        type="email"
        value={form.email}
        onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
        className="w-full px-3 py-2 border border-gray-300 rounded-md"
        required
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Fler e-postadresser</label>
      <input
        type="text"
        value={form.additionalEmails}
        onChange={(e) => setForm(f => ({ ...f, additionalEmails: e.target.value }))}
        className="w-full px-3 py-2 border border-gray-300 rounded-md"
        placeholder="Kommaseparerade, t.ex. a@x.se, b@x.se"
      />
      <p className="text-xs text-gray-400 mt-1">Mailet skickas till alla angivna adresser.</p>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
      <input
        type="text"
        value={form.phone}
        onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
        className="w-full px-3 py-2 border border-gray-300 rounded-md"
      />
    </div>

    <label className="flex items-center gap-2 text-sm text-gray-700">
      <input
        type="checkbox"
        checked={form.isActive}
        onChange={(e) => setForm(f => ({ ...f, isActive: e.target.checked }))}
        className="rounded border-gray-300"
      />
      Aktiv (visas i listan)
    </label>

    <div className="flex gap-3 pt-2">
      <button
        type="button"
        onClick={onCancel}
        className="flex-1 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
      >
        Avbryt
      </button>
      <button
        type="submit"
        disabled={saving}
        className="flex-1 px-3 py-2 text-sm text-white bg-leadme-pink rounded-lg hover:bg-leadme-pink-dark disabled:opacity-50"
      >
        {saving ? 'Sparar…' : 'Spara'}
      </button>
    </div>
  </form>
);

export default ProjektorContactForm;
